/**
 * Copyright (c) 2022-present, NDLA.
 *
 * This source code is licensed under the GPLv3 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import {
  Badge,
  ListItemContent,
  ListItemHeading,
  ListItemImage,
  ListItemRoot,
  type ListItemVariantProps,
  Skeleton,
  Text,
} from "@ndla/primitives";
import { SafeLink } from "@ndla/safelink";
import { styled } from "@ndla/styled-system/jsx";
import { linkOverlay } from "@ndla/styled-system/patterns";
import { BadgesContainer } from "@ndla/ui";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useListItemTraits } from "../../util/listItemTraits";
import { ContentTypeFallbackIcon } from "../ContentTypeFallbackIcon";

const StyledListItemContent = styled(ListItemContent, {
  base: {
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    gap: "4xsmall",
    width: "full",
  },
});

const StyledBadgesContainer = styled(BadgesContainer, {
  base: {
    marginBlockStart: "xsmall",
  },
});

export interface ListResourceProps {
  id: string;
  link: string;
  title: string;
  resourceImage: { src: string | undefined; alt: string };
  resourceTypes?: { id: string; name: string }[];
  description?: string;
  menu?: ReactNode;
  traits?: string[] | null;
  storedResourceType?: string;
  isLoading?: boolean;
  isSelected?: boolean;
}

const StyledDescription = styled(Text, {
  base: {
    width: "full",
    lineClamp: "2",
    overflowWrap: "anywhere",
  },
});

const ActionWrapper = styled("div", {
  base: {
    marginInlineStart: "auto",
    "& > button, & > a": {
      position: "relative",
    },
  },
});

const DescriptionWrapper = styled("div", {
  base: {
    display: "flex",
    justifyContent: "space-between",
    gap: "3xsmall",
    width: "full",
  },
});

const StyledListItemImage = styled(ListItemImage, {
  base: {
    tabletDown: {
      display: "none",
    },
  },
  variants: {
    isFallback: {
      true: {
        background: "surface.subtle",
        border: "1px solid",
        borderColor: "stroke.discrete",
        borderRadius: "xsmall",
      },
    },
  },
});

const StyledListItemHeading = styled(ListItemHeading, {
  base: {
    lineClamp: "2",
    overflowWrap: "anywhere",
  },
});

const LoadingListItemRoot = styled(ListItemRoot, {
  base: {
    width: "full",
    pointerEvents: "none",
  },
});

const StyledListItemRoot = styled(ListItemRoot, {
  base: {
    width: "full",
    tabletDown: {
      "& picture": {
        display: "none",
      },
    },
  },
  variants: {
    isSelected: {
      true: {
        backgroundColor: "surface.action.brand.1.selected",
      },
      false: {},
    },
  },
});

const StyledContentTypeFallbackIcon = styled(ContentTypeFallbackIcon, {
  base: {
    tabletDown: {
      display: "none",
    },
  },
});

export const ListResource = ({
  id,
  link,
  title,
  resourceImage,
  resourceTypes,
  description,
  menu,
  traits,
  storedResourceType,
  isLoading = false,
  isSelected,
  nonInteractive,
}: ListResourceProps & ListItemVariantProps) => {
  const { t } = useTranslation();

  const listItemTraits = useListItemTraits({ traits, resourceTypes, resourceType: storedResourceType });

  if (isLoading) {
    return (
      <LoadingListItemRoot aria-label={t("loading")} aria-busy={true} nonInteractive={nonInteractive}>
        <Skeleton>
          <ListItemImage alt="" />
        </Skeleton>
        <StyledListItemContent>
          <Skeleton css={{ width: "2/5" }}>
            <ListItemHeading>&nbsp;</ListItemHeading>
          </Skeleton>
          <Skeleton>{<Badge size="small">{t("contentTypes.missing")}</Badge>}</Skeleton>
        </StyledListItemContent>
      </LoadingListItemRoot>
    );
  }

  return (
    <StyledListItemRoot id={id} nonInteractive={nonInteractive} isSelected={isSelected}>
      {/* TODO: Variants */}
      <StyledListItemImage
        src={resourceImage.src}
        alt=""
        isFallback={!resourceImage.src}
        fallbackElement={<StyledContentTypeFallbackIcon contentType={storedResourceType} />}
      />
      <StyledListItemContent>
        {nonInteractive ? (
          <StyledListItemHeading color={!storedResourceType ? "text.subtle" : undefined}>{title}</StyledListItemHeading>
        ) : (
          <StyledListItemHeading
            asChild
            consumeCss
            css={linkOverlay.raw()}
            color={!storedResourceType ? "text.subtle" : undefined}
          >
            <SafeLink to={link}>{title}</SafeLink>
          </StyledListItemHeading>
        )}

        <DescriptionWrapper>
          {!!description && <StyledDescription>{description}</StyledDescription>}
          <ActionWrapper>{menu}</ActionWrapper>
        </DescriptionWrapper>
        <StyledBadgesContainer>
          {listItemTraits.map((trait) => (
            <Badge size="small" key={`${id}-${trait}`}>
              {trait}
            </Badge>
          ))}
        </StyledBadgesContainer>
      </StyledListItemContent>
    </StyledListItemRoot>
  );
};
